import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { AppConfigService } from '../providers/config.service';
import { StorageService } from './storage.service';

declare global {
  interface Window {
    featureFlags: {
      get: (flag: string) => boolean;
      state: (env: string) => string[];
      all: () => string[];
      enable: (flag: string) => string;
      disable: (flag: string) => string;
      reset: () => string;
      set: (env: string) => string;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  availableFeatureFlags: string[] = [
    'testFlag',
    'MAINTENANCE_MODE',
    'blockGalleryViews', // Block gallery view reporting
    'forceGalleryViews', // Will report gallery views even when using one of the blacklisted email addresses
    'assetDebugMode',
    'websocketTest',
    'serverTiming',
    'alwaysShowAssetPageUi',
    'allowDebugSetDigestToInstantForGuest',

    'translations',
    'assetTags',
    'convertSectionToSubGallery',
    'noTrial',
    'shortTrial',
    'commentsV2Testing',
    'bigFileUpload',
  ];
  private flags: Set<string>;

  constructor(
    private config: AppConfigService,
    private storage: StorageService,
    @Inject(PLATFORM_ID) private platformId: string,
    private route: ActivatedRoute
  ) {
    this.initFeatureFlags();
    this.initWindowFeatureFlags();
    this.route.queryParams.subscribe(queryParams => {
      this.checkQuery(queryParams);
    });
    this.checkQuery(this.route.snapshot.queryParams);
  }

  checkQuery(queryParams: Params) {
    if (queryParams.ff) {
      const flags: string[] = queryParams.ff.split(',');
      flags.forEach(flag => console.log(this.enable(flag)));
    }
    if (queryParams.dff) {
      const flags: string[] = queryParams.ff.split(',');
      flags.forEach(flag => console.log(this.disable(flag)));
    }
  }

  private initFeatureFlags() {
    const overrides = this.getOverrides();
    this.flags = new Set(this.getEnvFlags());
    this.availableFeatureFlags.forEach(flag => {
      if (overrides[flag] === true) this.flags.add(flag);
      else if (overrides[flag] === false) this.flags.delete(flag);
    });
  }

  private initWindowFeatureFlags() {
    if (!isPlatformBrowser(this.platformId)) return;
    window.featureFlags = {
      get: (flag: string) => {
        return this.get(flag);
      },
      state: (env: string) => {
        if (env) return this.getEnvFlags(env);
        return Array.from(this.flags.values());
      },
      all: () => {
        return this.availableFeatureFlags;
      },
      enable: (flag: string) => {
        return this.enable(flag);
      },
      disable: (flag: string) => {
        return this.disable(flag);
      },
      reset: () => {
        this.reset();
        return 'All feature flag overrides removed';
      },
      set: (env: string) => {
        const flags = this.getEnvFlags(env);
        if (!flags) return '"' + env + '" is not a valid environment. Use "local", "dev", "test" or "production"';
        let changed = false;
        this.flags.forEach(flag => {
          if (!flags.includes(flag)) {
            console.log('disabling ' + flag);
            changed = true;
            this.disable(flag);
          }
        });
        flags.forEach(flag => {
          if (!this.flags.has(flag)) {
            console.log('enabling ' + flag);
            changed = true;
            this.enable(flag);
          }
        });
        if (changed) return 'Feature flags set as they are in "' + env + '"';
        else return 'Feature flags already match "' + env + '"';
      },
    };
  }

  public enable(flag: string) {
    if (!this.availableFeatureFlags.includes(flag))
      return `"${flag}" is not a valid flag. Use featureFlags.all() to sees all available flags`;
    this.flags.add(flag);
    const overrides = this.getOverrides();
    overrides[flag] = true;
    if (isPlatformServer(this.platformId)) return;
    this.storage.set('featureFlagOverrides', overrides);
    return flag + ' enabled';
  }

  public disable(flag: string) {
    if (!this.availableFeatureFlags.includes(flag))
      return `"${flag}" is not a valid flag. Use featureFlags.all() to sees all available flags`;
    this.flags.delete(flag);
    const overrides = this.getOverrides();
    overrides[flag] = false;
    if (isPlatformServer(this.platformId)) return;
    this.storage.set('featureFlagOverrides', overrides);
    return flag + ' disabled';
  }

  public get(flag: string) {
    return this.flags.has(flag);
  }

  public state() {
    return Array.from(this.flags.values());
  }

  public reset() {
    this.flags = new Set(this.getEnvFlags());
    this.storage.remove('featureFlagOverrides');
  }

  getEnvFlags(env: string = this.config.env): string[] {
    if (env === 'local' || env === 'dev') {
      return [
        'translations',
        'assetTags',
        'convertSectionToSubGallery',
        'forceGalleryViews',
        'shortTrial',
        'serverTiming',
        'allowDebugSetDigestToInstantForGuest',
        // 'commentsV2Testing',
        // 'bigFileUpload',
      ];
    }
    if (env === 'production') {
      return ['shortTrial'];
    }
  }

  private getOverrides(): { [propName: string]: boolean } {
    return this.storage.get('featureFlagOverrides') || {};
  }
}
